'use client';

import { useEffect, useState } from 'react';
import { getBasePath } from '../utils/basePath';
import { fetchJsonOnce, fetchJsonWithBackoff } from './fetchJson';
import type { YouTubeItem } from './MediaItemTypes';
import { sortByNewest } from './sortByNewest';

export const fallbackYoutubeVideos: YouTubeItem[] = [
  {
    id: 'M1V2uaX8MVM',
    title: 'Rusty',
    url: 'https://www.youtube.com/watch?v=M1V2uaX8MVM',
    type: 'track',
    date: '2020-11-01T00:00:00.000Z',
    source: 'youtube',
    checked: false,
    show: true,
    curated: true,
  },
  {
    id: 'JI9ecHV58js',
    title: 'Middle of the Night',
    url: 'https://www.youtube.com/watch?v=JI9ecHV58js',
    type: 'track',
    date: '2020-11-01T00:00:00.000Z',
    source: 'youtube',
    checked: false,
    show: true,
    curated: true,
  },
];

/**
 * Normalizes a raw input object into a typed YouTubeItem.
 */
function normalizeYouTubeItem(input: Partial<YouTubeItem>): YouTubeItem | null {
  const url = input.url || '';
  const idFromUrl = (() => {
    try {
      return new URL(url).searchParams.get('v') || undefined;
    } catch {
      return undefined;
    }
  })();

  const id = input.id || idFromUrl;
  if (!id || !url) return null;

  return {
    id,
    title: input.title || `YouTube video ${id}`,
    url,
    type: input.type || 'track',
    date: input.date || null,
    source: 'youtube',
    checked: input.checked ?? false,
    show: input.show ?? true,
    curated: input.curated ?? false,
  };
}

/**
 * React hook for fetching and managing YouTube items.
 */
export function useYouTubeItems() {
  const [items, setItems] = useState<YouTubeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const fetcher = retryToken > 0 ? fetchJsonWithBackoff : fetchJsonOnce;
      const basePath = getBasePath();
      const result = await fetcher<{ videos?: Partial<YouTubeItem>[] }>(
        `${basePath}/data/youtube-videos.json`
      );

      if (!result.ok) {
        setItems(sortByNewest(fallbackYoutubeVideos));
        setError(result.reason === 'exception' ? result.error : null);
        setLoading(false);
        return;
      }

      const videos = Array.isArray(result.data.videos) ? result.data.videos : [];
      const normalized = videos
        .map((video) => normalizeYouTubeItem(video))
        .filter((video): video is YouTubeItem => Boolean(video));

      setItems(sortByNewest(normalized.length ? normalized : fallbackYoutubeVideos));
      setError(null);
      setLoading(false);
    };

    load();
  }, [retryToken]);

  const retry = () => setRetryToken((token) => token + 1);

  return {
    items,
    loading,
    error,
    retry,
  };
}
